import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { type FC, useEffect, useState } from "react";

import {
	type DocumentPageMetadata,
	type PageMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";
import { ChevronRightIcon } from "@moment/icons";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	handleIframeNavigation,
	invokePostMessage,
	isRunningInIframe,
	useIsMounted,
} from "@moment/ui";

import { type TreeItems } from "../../navigation-pane/types";

/**
 * Hook to safely check if running in iframe after hydration.
 * Returns false during SSR and initial hydration to avoid mismatch.
 */
const useIsInIframe = () => {
	const isMounted = useIsMounted();
	return isMounted && isRunningInIframe();
};

const buildNestedSlugPath = (parentSlugPath: string, slug: string) =>
	parentSlugPath ? `${parentSlugPath}/${slug}` : slug;

/**
 * This horrible component exists because `SidebarMenuSub` is a different thing than `SidebarMenu`,
 * and therefore must be the root of recursion. Ideally, we would recurse in `SidebarMenuItemTree`.
 */
const RecursiveSubMenu: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTreeChildren: TreeItems<PageMetadata>;
	urlPrefix: string;
	isInIframe: boolean;
	parentSlugPath: string;
}> = ({
	workspaceName,
	documentId,
	activePage,
	pageTreeChildren,
	urlPrefix,
	isInIframe,
	parentSlugPath,
}) => {
	const router = useRouter();

	return pageTreeChildren?.length ? (
		<>
			<CollapsibleTrigger asChild>
				<SidebarMenuAction className="data-[state=open]:rotate-90">
					<ChevronRightIcon />
					<span className="sr-only">Toggle</span>
				</SidebarMenuAction>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<SidebarMenuSub>
					{pageTreeChildren?.map((subItem) => {
						const slugPath = buildNestedSlugPath(parentSlugPath, subItem.data.slug);
						const href = slugPath === "index" ? urlPrefix : `${urlPrefix}/${slugPath}`;

						return (
							<Collapsible key={subItem.id} asChild defaultOpen>
								<SidebarMenuSubItem key={subItem.data.title}>
									<SidebarMenuSubButton
										isActive={subItem.id === activePage?.id}
										asChild
										className={cn({
											"font-bold ": subItem.id === activePage?.id,
										})}
									>
										{isInIframe ? (
											<a
												href={href}
												onClick={async (e) =>
													await handleIframeNavigation(
														e,
														{
															workspaceName,
															documentId,
															pageSlug: slugPath,
														},
														router
													)
												}
											>
												{subItem.data.title}
											</a>
										) : (
											<Link href={href}>
												<span>{subItem.data.title}</span>
											</Link>
										)}
									</SidebarMenuSubButton>
									{subItem.children?.length ? (
										<RecursiveSubMenu
											workspaceName={workspaceName}
											documentId={documentId}
											activePage={activePage}
											pageTreeChildren={subItem.children}
											urlPrefix={urlPrefix}
											isInIframe={isInIframe}
											parentSlugPath={slugPath}
										/>
									) : null}
								</SidebarMenuSubItem>
							</Collapsible>
						);
					})}
				</SidebarMenuSub>
			</CollapsibleContent>
		</>
	) : null;
};

export const SidebarMenuItemTree: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTree: TreeItems<PageMetadata>;
	publishSettings: PublishSettings | undefined;
}> = ({ workspaceName, documentId, activePage, pageTree, publishSettings }) => {
	const [urlPrefix, setUrlPrefix] = useState(
		makeUrlPrefix(workspaceName, documentId, publishSettings)
	);
	const router = useRouter();
	// Use hook to avoid hydration mismatch - isRunningInIframe() returns false on server
	const isInIframe = useIsInIframe();

	useEffect(() => {
		// Safe to call isRunningInIframe() here since useEffect only runs on client
		if (!isRunningInIframe()) {
			return;
		}

		invokePostMessage<string>("ping")
			.then((parentOrigin?: string) => {
				if (parentOrigin && urlPrefix.startsWith("/")) {
					setUrlPrefix(
						`${parentOrigin}${makeUrlPrefix(workspaceName, documentId, undefined)}`
					);
				}
			})
			.catch((err) => console.error("Error sending ping request to the parent frame", err));
	}, [router.query, setUrlPrefix, workspaceName, documentId, urlPrefix]);

	return (
		<div>
			{pageTree.map((page) => {
				const slugPath = page.data.slug;
				const isIndex = slugPath === "index";
				const href = isIndex ? urlPrefix : `${urlPrefix}/${slugPath}`;

				// LAME BUT IMPORTANT:
				//
				// 1. Cause the Next page router to behave well with custom
				// domains. If we are routing, say, `moment.dev/docs`, then the index page will be
				// `/docs`. If you use a `Link`, Next page router will try to route to a named route
				// called `/docs`. We don't want that, we want to route to the index page.
				//
				//
				// 2. For iframes, we will always use anchor tags with an href that
				// uses the parent frame's URL, so the links are shareable and attribute
				// to the correct domain. But on the client-side, we'll intercept the click
				// and route to the right page ourselves.
				const pageLink =
					isIndex || isInIframe ? (
						<a
							href={href}
							onClick={async (e) =>
								await handleIframeNavigation(
									e,
									{ workspaceName, documentId, pageSlug: slugPath },
									router
								)
							}
						>
							{page.data.title}
						</a>
					) : (
						<Link href={href}>{page.data.title}</Link>
					);

				return (
					<Collapsible
						key={page.id}
						asChild
						defaultOpen
						//defaultOpen={item.isActive}
					>
						<SidebarMenuItem>
							<SidebarMenuButton
								isActive={page.id === activePage?.id}
								asChild
								className={cn({ "font-bold ": page.id === activePage?.id })}
							>
								{pageLink}
							</SidebarMenuButton>
							<RecursiveSubMenu
								workspaceName={workspaceName}
								documentId={documentId}
								activePage={activePage}
								pageTreeChildren={page.children}
								urlPrefix={urlPrefix}
								isInIframe={isInIframe}
								parentSlugPath={slugPath}
							/>
						</SidebarMenuItem>
					</Collapsible>
				);
			})}
		</div>
	);
};
